import React from "react";
import { useTranslator } from "@brickme/frontend-common/src/i18n/translation.tsx";
import pluralize from "pluralize";
import Modal from "~/components/modal.tsx";
import useAppDispatch from "~/hooks/use-app-dispatch.ts";
import useAppSelector from "~/hooks/use-app-selector.ts";
import { setGiftRedemptionStep } from "../store-slice.ts";
import { selectGiftError, selectGiftRedemption } from "../selectors.ts";
import { selectUser } from "~/features/user/selectors.ts";
import GiftGridVisual from "./gift-grid-visual.tsx";

type GiftCongratulationsModalProps = {
	readonly show: boolean;
};

function GiftCongratulationsModal({ show }: GiftCongratulationsModalProps) {
	const t = useTranslator();
	const dispatch = useAppDispatch();
	const giftRedemption = useAppSelector(selectGiftRedemption);
	const giftError = useAppSelector(selectGiftError);
	const user = useAppSelector(selectUser);

	const onStartCreating = () => {
		// If user is already signed in, skip authentication and go directly to configuring
		if (user.type === "signedIn") {
			dispatch(
				setGiftRedemptionStep({
					type: "configuring",
					giftFramingVariantId: undefined,
					giftFramingVariantQty: undefined,
				}),
			);
		} else {
			// User needs to authenticate first
			dispatch(setGiftRedemptionStep({ type: "showName" }));
		}
	};

	// Show error state if there's an error
	if (giftError) {
		return (
			<Modal title={t("Gift Redemption Error")} show={true} slimBorder>
				<Modal.Text>{giftError}</Modal.Text>
				<Modal.Actions
					actions={[
						{
							text: t("Go to Homepage"),
							href: "/",
							type: "ghost",
						},
					]}
				/>
			</Modal>
		);
	}

	// Show congratulations if no error and we have gift details
	if (!giftRedemption?.giftSize) {
		return null;
	}

	const giftSizeDisplay = `${giftRedemption.giftSize.width}x${giftRedemption.giftSize.height}`;
	const totalCells =
		giftRedemption.giftSize.width * giftRedemption.giftSize.height;

	return (
		<Modal show={show} slimBorder>
			<div className="gift-congratulations">
				<div className="gift-congratulations-header">
					<div className="gift-congratulations-title">
						{t("🎉 Congratulations!")}
					</div>
					<div className="gift-congratulations-subtitle">
						{t("You've received a special gift")}
					</div>
				</div>

				<GiftGridVisual
					width={giftRedemption.giftSize.width}
					height={giftRedemption.giftSize.height}
				/>

				<div className="gift-congratulations-details">
					<div className="gift-project-title">
						{t("{size} Brick Me Project", { size: giftSizeDisplay })}
					</div>
					<div className="gift-project-subtitle">
						{t("{baseplates} included in your gift", {
							baseplates: pluralize(t("baseplate"), totalCells, true),
						})}
					</div>
					<div className="gift-free-message">
						{t("✨ This size is")} <strong>{t("completely free")}</strong>
						{t("! You can upgrade to larger sizes by paying the difference.")}
					</div>
				</div>
			</div>

			<Modal.Actions
				actions={[
					{
						text: t("🎨 Start Creating"),
						onClick: onStartCreating,
					},
				]}
			/>
		</Modal>
	);
}

export default GiftCongratulationsModal;
